import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, Eye, Clock, Shield } from 'lucide-react'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { useToast } from '@/hooks/use-toast'

interface StartImpersonationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  churchId: string
  churchName: string
}

export default function StartImpersonationDialog({
  open,
  onOpenChange,
  churchId,
  churchName,
}: StartImpersonationDialogProps) {
  const [reason, setReason] = useState('')
  const [isStarting, setIsStarting] = useState(false)
  const { startImpersonation } = useImpersonation()
  const { toast } = useToast()

  async function handleStartImpersonation() {
    if (reason.length < 10) {
      toast({
        title: 'Invalid Reason',
        description: 'Please provide a detailed reason (minimum 10 characters)',
        variant: 'destructive',
      })
      return
    }

    setIsStarting(true)

    const success = await startImpersonation(churchId, churchName, reason)

    if (success) {
      toast({
        title: 'Impersonation Started',
        description: `You are now viewing as ${churchName}. Session expires in 30 minutes.`,
      })
      onOpenChange(false)
      setReason('')

      // Reload to apply impersonation context
      window.location.href = '/dashboard'
    } else {
      toast({
        title: 'Failed to Start Impersonation',
        description: 'An error occurred. Please try again.',
        variant: 'destructive',
      })
    }

    setIsStarting(false)
  }

  function handleCancel() {
    setReason('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-destructive" />
            Start Church Impersonation
          </DialogTitle>
          <DialogDescription>
            You are about to view the platform as <strong>{churchName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Security Warning */}
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Security Notice:</strong> This session will be fully audited. All actions will be logged
              and attributed to your account.
            </AlertDescription>
          </Alert>

          {/* Session Details */}
          <div className="space-y-2 rounded-lg border p-4 bg-muted/50">
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Session Duration:</span>
              <span>30 minutes (auto-logout)</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Access Level:</span>
              <span>Church Admin permissions</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Eye className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Tracking:</span>
              <span>All pages and actions logged</span>
            </div>
          </div>

          {/* Reason Field */}
          <div className="space-y-2">
            <Label htmlFor="reason">
              Reason for Impersonation <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="reason"
              placeholder="e.g., Troubleshooting payment processing issue reported by church admin..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Minimum 10 characters. Be specific about why you need to impersonate this church.
            </p>
            {reason.length > 0 && reason.length < 10 && (
              <p className="text-xs text-destructive">
                {10 - reason.length} more characters required
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isStarting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleStartImpersonation}
            disabled={isStarting || reason.length < 10}
          >
            {isStarting ? 'Starting...' : 'Start Impersonation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
